import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { FiClock } from "react-icons/fi";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const TimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  font-family: "Poppins", sans-serif;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #444;
  font-weight: 500;
`;

const TimeDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.8rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.95rem;
  color: #222;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #aaa;
  }

  ${({ $isOpen }) =>
        $isOpen &&
        `
    border-color: #ff0102;
    box-shadow: 0 0 0 2px rgba(255,1,2,0.1);
  `}
`;

const ClockIcon = styled(FiClock)`
  color: #ff0102;
  font-size: 18px;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 65px;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  z-index: 20;
  padding: 10px;
  animation: ${fadeIn} 0.15s ease-out;
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
  padding-right: 4px;
`;

const TimeOption = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? "#ff0102" : "#f8f8f8")};
  color: ${({ $active }) => ($active ? "#fff" : "#333")};
  font-size: 0.9rem;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "#ff0102" : "#eee")};
  }
`;

export default function TimePicker({ label, value, onChange, minTime = "00:00", maxTime = "23:59" }) {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(value || "");
    const wrapperRef = useRef();

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const generateTimes = () => {
        const times = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 30) {
                const hour = String(h).padStart(2, "0");
                const minute = String(m).padStart(2, "0");
                const timeStr = `${hour}:${minute}`;
                times.push(timeStr);
            }
        }
        return times.filter((t) => t >= minTime && t <= maxTime);
    };

    const handleSelect = (time) => {
        setSelected(time);
        onChange?.(time);
        setIsOpen(false);
    };

    return (
        <TimeWrapper ref={wrapperRef}>
            {label && <Label>{label}</Label>}
            <TimeDisplay onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
                <span>{selected || label}</span>
                <ClockIcon />
            </TimeDisplay>

            {isOpen && (
                <Dropdown>
                    <TimeGrid>
                        {generateTimes().map((time) => (
                            <TimeOption
                                key={time}
                                $active={time === selected}
                                onClick={() => handleSelect(time)}
                            >
                                {time}
                            </TimeOption>
                        ))}
                    </TimeGrid>
                </Dropdown>
            )}
        </TimeWrapper>
    );
}
